// tiltRotateControl — Neigen + Rotation als custom topo2-Control (ersetzt Googles eingebauten `rotateControl`).
//
// Verantwortung: NUR UI + Aktion + Sichtbarkeit. Eigener `topo2-btn-stack` (RIGHT_BOTTOM, unter Zoom — eigener Stack,
// damit Gruppen später leicht verschiebbar sind). Reihenfolge oben→unten: Rechts-drehen, Links-drehen, Neigen-Toggle.
//
// Sichtbarkeit (wie Googles Original, das nur bei nahem Zoom erscheint):
//   - ganzes Menü NUR bei kippbarem Kartentyp (Satellit/45°-Luftbild) — Roadmap kann nicht kippen;
//   - sonst erst ab `TILT_MIN_ZOOM` (oder solange geneigt → man kann immer wieder flach machen);
//   - die beiden Dreh-Buttons NUR wenn geneigt (Tilt > 0) — auf flacher Karte ist Drehen sinnlos.
//
// Die KARTE ist die Wahrheit: Klick → map.setHeading/setTilt; Spiegelung via `tilt_changed`/`zoom_changed`. Googles
// Control wird lokal via map.setOptions abgeschaltet (Admin-Pool unberührt). KEIN Store-Bezug.
import { resolveControlPosition, resolveMapType } from "../adapter/providerConstants.js";
import { str } from "../../i18n/str.js";
// topo2 Button-Basis (Buttons + Stack). Co-located: reist mit dem Consumer; Vite dedupliziert.

const HEADING_STEP = 90;    // Grad pro Klick — 45°-Luftbild rastet auf 90°-Schritte (N/O/S/W); 45 würde asymmetrisch runden
const TILT_ON = 45;         // Neige-Winkel im aktiven Zustand (45°-Imagery)

// Icons (currentColor, Linien-Stil wie die übrigen Map-Icons).
const ICON_ROTATE_RIGHT =
    '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M19 12a7 7 0 1 1-2-4.9"/><polyline points="19 4 19 8 15 8"/></g></svg>';
const ICON_ROTATE_LEFT =
    '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M5 12a7 7 0 1 0 2-4.9"/><polyline points="5 4 5 8 9 8"/></g></svg>';
const ICON_TILT =       // geneigte Ebene (Tilt einschalten)
    '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + '<polygon points="3 17 8 7 21 7 16 17" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';
const ICON_TILT_FLAT =  // flache Ebene (aktiver Zustand → „flach machen")
    '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + '<rect x="4" y="9" width="16" height="6" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';

export function createTiltRotateControl({ map, google }) {
    const container = document.createElement('div');
    container.className = 'topo-tilt-rotate-controls topo2-btn-stack';

    const makeButton = (variantClass, icon, label, onClick) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = variantClass + ' topo2-btn topo2-btn--icon topo2-btn--outline';
        b.title = label;
        b.setAttribute('aria-label', label);
        b.innerHTML = icon;
        b.addEventListener('click', onClick);
        return b;
    };

    const heading = () => (map.getHeading && map.getHeading()) || 0;
    const tiltDeg = () => (map.getTilt && map.getTilt()) || 0;
    const normHeading = (deg) => ((deg % 360) + 360) % 360;   // 0..359 (kein negatives setHeading → Google ignoriert das sonst)

    // Google: höherer heading = Ansicht dreht GEGEN den Uhrzeigersinn. Für „nach rechts" (im Uhrzeigersinn) also heading−.
    const rotateRight = makeButton('topo-rotate-right', ICON_ROTATE_RIGHT, str('MAP_ROTATE_RIGHT'),
        () => { if (map.setHeading) { map.setHeading(normHeading(heading() - HEADING_STEP)); } });
    const rotateLeft = makeButton('topo-rotate-left', ICON_ROTATE_LEFT, str('MAP_ROTATE_LEFT'),
        () => { if (map.setHeading) { map.setHeading(normHeading(heading() + HEADING_STEP)); } });
    // Chef-Wunsch (3): Tilt NUR per Button. `userTilted` = die Absicht; Googles Auto-Kippen (45°-Imagery bei hohem Zoom)
    // wird unterdrückt, solange der User nicht bewusst über den Button geneigt hat.
    let userTilted = false;
    let available = false;   // 45°-Luftbild an der aktuellen Ansicht verfügbar? (synchron geprobt, s.u.)
    let probing = false;     // Re-Entrancy-Schutz: das setTilt im Probe feuert tilt_changed → sync soll das ignorieren
    let lastProbedKey = null;   // Signatur des zuletzt geprobten Ausschnitts (Zoom|Typ|Bounds) → Dedupe gegen die GetViewportInfo-Schleife

    const tilt = makeButton('topo-tilt', ICON_TILT, str('MAP_TILT'),
        () => {
            userTilted = !userTilted;
            if (map.setTilt) { map.setTilt(userTilted ? TILT_ON : 0); }
        });

    container.appendChild(rotateRight);   // oben
    container.appendChild(rotateLeft);
    container.appendChild(tilt);          // unten

    // 45°-Verfügbarkeit SYNCHRON proben: auf Raster meldet `getTilt()` sofort 45 (Luftbild vorhanden) bzw. 0 (nicht) —
    // getestet 2026-07-01. Also setTilt(45) → sofort lesen → sofort zurück (kein sichtbarer Kipp, da im selben Tick).
    // Nur proben, wenn flach; ist schon (bewusst) geneigt, ist 45° offensichtlich verfügbar → nicht anfassen.
    const probeAvailability = () => {
        if (!map.setTilt || !map.getTilt) { available = false; return; }
        const cur = map.getTilt();
        if (cur === TILT_ON) { available = true; return; }
        probing = true;
        map.setTilt(TILT_ON);
        available = (map.getTilt() === TILT_ON);
        map.setTilt(cur);
        probing = false;
    };

    // Spiegelt Zustand → Sichtbarkeit + Neigen-Button-Optik. sync() = nur Optik/Roadmap (an tilt_changed); refresh() probt (an idle/maptypeid).
    const sync = () => {
        if (probing) { return; }   // tilt_changed aus dem Probe-Roundtrip ignorieren (sonst Rekursion)
        // Google macht beim Rauszoomen (45° verschwindet) die Karte AUTO-flach → getTilt()===0, obwohl userTilted. Das ist das
        // Signal „hier kein 45° mehr": Absicht + Verfügbarkeit zurücknehmen, sonst bliebe der Button auf „geneigt" stehen.
        if (userTilted && map.getTilt && map.getTilt() === 0) { userTilted = false; available = false; }
        // Roadmap kann nicht kippen → bei Wechsel AUF Roadmap einen aktiven Tilt zurücknehmen (sonst bliebe userTilted hängen).
        const canTilt = !map.getMapTypeId || map.getMapTypeId() !== resolveMapType(google, 'roadmap');
        if (!canTilt && userTilted) { userTilted = false; if (map.setTilt) { map.setTilt(0); } }
        const tilted = userTilted;   // Absicht steuert die UI (map.getTilt kann bei Raster auto-0 sein, obwohl userTilted)

        // Menü: nur wenn 45° hier TATSÄCHLICH verfügbar ist (Probe) — oder solange (bewusst) geneigt, damit man wieder flach
        // machen kann. Ersetzt die alte reine Zoom-Schwelle, die den Button auch dort zeigte, wo nichts kippte (Chef-Verwirrung).
        container.style.display = ((available && canTilt) || tilted) ? '' : 'none';
        // Dreh-Buttons: nur wenn geneigt.
        rotateRight.style.display = tilted ? '' : 'none';
        rotateLeft.style.display = tilted ? '' : 'none';
        // Neigen-Button: aktiver Zustand + Icon-Wechsel.
        tilt.setAttribute('aria-pressed', tilted ? 'true' : 'false');
        tilt.innerHTML = tilted ? ICON_TILT_FLAT : ICON_TILT;
    };

    // Ausschnitt-Signatur (Zoom · Kartentyp · gerundete Bounds). Bei GLEICHEM Ausschnitt NICHT neu proben: das idle, das
    // unsere EIGENE setTilt-Probe auslöst, hat denselben Ausschnitt → sonst Endlos-Schleife (setTilt triggert Googles
    // Oblique-Fetch `GetViewportInfo` → idle → probe → setTilt → …, dort wo 45° existiert, z.B. Wien). Nur echter Pan/Zoom/Typwechsel probt.
    const viewportKey = () => {
        const z = (map.getZoom && map.getZoom()) || 0;
        const t = (map.getMapTypeId && map.getMapTypeId()) || '';
        const b = map.getBounds && map.getBounds();
        let bb = '';
        if (b && b.getSouthWest && b.getNorthEast) {
            const sw = b.getSouthWest(), ne = b.getNorthEast();
            bb = [sw.lat(), sw.lng(), ne.lat(), ne.lng()].map((n) => n.toFixed(4)).join(',');
        }
        return z + '|' + t + '|' + bb;
    };
    // Verfügbarkeit neu proben + spiegeln — an `idle`/`maptypeid_changed`. Dedupe: gleicher Ausschnitt → nur spiegeln, nicht proben.
    const refresh = () => {
        const key = viewportKey();
        if (key === lastProbedKey) { sync(); return; }
        lastProbedKey = key;
        probeAvailability();
        sync();
    };

    return { container, sync, refresh };
}

// Modul-Factory für den MapUiReconciler: baut den Stack, schaltet Googles Control lokal ab, spiegelt Zustand, Handle { attach, destroy }.
export function createTiltRotateControlModule({ map, google, position }) {
    const control = createTiltRotateControl({ map, google });


    map.setOptions({ rotateControl: false });   // Googles Control auf DIESER Karte aus
    // Chef-Wunsch (3): setTilt(0) = IMMER flach (Google-Semantik: kein Auto-45 bei hohem Zoom). Kippen nur per Button.
    if (map.setTilt) { map.setTilt(0); }
    const listeners = [
        google.maps.event.addListener(map, 'tilt_changed', control.sync),        // Optik + Auto-Flatten-Erkennung (kein Re-Probe → keine Rekursion)
        google.maps.event.addListener(map, 'idle', control.refresh),             // NUR idle proben — NICHT zoom_changed: die Probe (setTilt 45→0) kippt sonst bei jedem Scroll-Schritt in nahen, 45°-fähigen Stufen kurz sichtbar
        google.maps.event.addListener(map, 'maptypeid_changed', control.refresh) // Typwechsel: neu proben (Roadmap → Menü weg)
    ];
    control.refresh();

    const pos = position !== undefined ? position : resolveControlPosition(google, 'RIGHT_BOTTOM');
    if (pos !== undefined) { map.controls[pos].push(control.container); }

    return {
        attach() {
            if (pos === undefined) { return; }
            const arr = map.controls[pos];
            if (!arr) { return; }
            for (let i = 0; i < arr.getLength(); i++) {
                if (arr.getAt(i) === control.container) { return; }
            }
            arr.push(control.container);
        },
        destroy() {
            for (const l of listeners) { if (l) { google.maps.event.removeListener(l); } }
            if (pos === undefined) { return; }
            const arr = map.controls[pos];
            if (!arr) { return; }
            for (let i = arr.getLength() - 1; i >= 0; i--) {
                if (arr.getAt(i) === control.container) { arr.removeAt(i); }
            }
        }
    };
}
